import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Routes, Route, Navigate, Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import PropTypes from 'prop-types';

import FeedFloTabs from '../../atoms/FeedFloTabs';
import Page from '../../atoms/Page';
import BarnSummaryTab from '../BarnSummaryTab';
import BarnConsumptionTab from '../BarnConsumptionTab';
import BarnSettingsTab from '../BarnSettingsTab';
import DeliveryTab from './DeliveryTab';
import DeviceTab from './DeviceTab';
import BinTab from './BinTab';
import InventoryTabV2 from '../InventoryTab';
import CommentsTab from '../CommentsTab';
import CalibrationTab from '../CalibrationTab';
import CreateCalibration from '../../organisms/CreateCalibration';
import CalibrationResultView from '../../organisms/CalibrationResultView';
import { censorTypes, useCensor } from '../../utils/hooks/useCensor';
import FeedLinePage from '../FeedLinePage';
import useUser from '../../utils/hooks/useUser';
import './BarnPage.scss';
import withStaffOnlyIdWrapper from '../../utils/withStaffOnlyIdWrapper';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import AnimalGroupPage from '../AnimalGroupPage';
import { BellIcon } from '../../atoms/Icons';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';
import Heading from '../../atoms/Heading';
import Select from '../../atoms/Select';
import Button from '../../atoms/Button';
import Dialog from '../../atoms/Dialog';
import { Formik } from 'formik';
import DateTimeInputField from '../../atoms/DateTimeInput/DateTimeInputField';
import { HTML_DATE_TIME_INPUT } from '../../utils/dates';
import useFeature from '../../utils/hooks/useFeature';
import NotificationSettings from './components/NotificationSettings';

const BARN_SUMMARY_GQL = gql`
  query BarnSummaryQuery($barn_id: uuid!, $user_id: uuid!, $now: bigint) {
    barn: farm_by_pk(id: $barn_id) {
      name
      organization_id
      organization {
        name
        user_notification_tier_assignments(where: { user_id: { _eq: $user_id } }) {
          id
          tier_id
          tier {
            name
          }
        }
        alert_policies(
          where: {
            alert_transport: { _in: ["sms", "email"] }
            user_id: { _eq: $user_id }
            status: { _eq: "active" }
            deleted_at: { _is_null: true }
          }
        ) {
          status
          alert_for
        }
      }
      feed_lines(where: { deleted_at: { _is_null: true } }, order_by: { name: asc }) {
        id
      }
      alert_policies(
        where: {
          alert_transport: { _in: ["sms", "email"] }
          user_id: { _eq: $user_id }
          status: { _eq: "active" }
          deleted_at: { _is_null: true }
        }
      ) {
        status
        alert_for
      }
      user_barn_alert_snoozes(
        where: {
          deleted_at: { _is_null: true }
          user_id: { _eq: $user_id }
          started_at: { _lte: $now }
          _or: [{ ended_at: { _gt: $now } }, { ended_at: { _is_null: true } }]
        }
      ) {
        id
        ended_at
      }
      user_notification_tier_assignments(where: { user_id: { _eq: $user_id } }) {
        id
        tier_id
        tier {
          name
        }
      }
      features_setting: barn_settings(
        where: {
          farm_id: { _eq: $barn_id }
          type: { _eq: "features" }
          version: { _eq: 0 }
          deleted_at: { _is_null: true }
        }
      ) {
        settings
      }
    }
  }
`;

const CREATE_SNOOZE_GQL = gql`
  mutation NewSnooze($barn_id: uuid!, $user_id: uuid!, $started_at: bigint!, $ended_at: bigint) {
    __typename
    insert_user_barn_alert_snooze_one(
      object: { barn_id: $barn_id, user_id: $user_id, started_at: $started_at, ended_at: $ended_at }
    ) {
      id
    }
  }
`;

const UPDATE_SNOOZE_GQL = gql`
  mutation UpdateSnoozes($ids: [uuid!]!, $ended_at: bigint) {
    __typename
    update_user_barn_alert_snooze(where: { id: { _in: $ids } }, _set: { ended_at: $ended_at }) {
      affected_rows
    }
  }
`;

function BarnPage({
  className = '',
  tabsClassName = '',
  barnIdOverride,
  openFaultIds = null,
  visibleFeedLineIds = null,
  dateRangeOverride = null,
}) {
  const { user } = useUser();
  const { active: showStaffUIFlag } = useFeature('SHOW_STAFF_ONLY_ELEMENTS');
  const [loadNow] = useState(dayjs.tz().unix());
  const { censor } = useCensor();

  const [animalGroupId, setAnimalGroupId] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const { barnId: paramsBarnId } = useParams();
  const barnId = barnIdOverride || paramsBarnId;

  const [notificationPopupOpen, setNotificationPopupOpen] = useState(false);

  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const customSnoozeDialog = useRef(null);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setNotificationPopupOpen(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupRef]);

  const { loading, data, refetch } = useQuery(BARN_SUMMARY_GQL, {
    variables: {
      barn_id: barnId,
      user_id: user.id,
      now: loadNow,
    },
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const [createSnooze] = useMutation(CREATE_SNOOZE_GQL, {
    variables: {
      user_id: user.id,
      barn_id: barnId,
    },
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const [updateSnooze] = useMutation(UPDATE_SNOOZE_GQL, {
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  // basically show the org name if you have access to multiple orgs via any method
  const showOrgName = user?.isStaff || user?.organizations?.length > 1 || user?.barns?.length > 1;

  const barn = data?.barn;
  const snoozedUntil = barn?.user_barn_alert_snoozes?.reduce((max, snooze) => {
    return max > snooze.ended_at ? max : snooze.ended_at;
  }, 0);
  const isStaff = user?.isStaff;
  const showStaffUI = isStaff && showStaffUIFlag;
  const binInventoryEnabled = barn?.features_setting?.[0]?.settings?.bin_inventory_enabled === true;
  const tabs = [
    {
      title: 'Status',
      link: 'status',
    },
    {
      title: 'Consumption',
      link: 'consumption',
    },
    ...(binInventoryEnabled
      ? [
        {
          title: 'Inventory',
          link: 'inventory',
        },
      ]
      : []),
    { title: 'Animal Groups', link: 'animals' },
    {
      title: 'Calibrations',
      link: 'calibrations',
    },
    { title: 'Settings', link: 'settings' },
    ...(showStaffUI ? [{ title: 'Devices', link: 'devices' }] : []),
    ...(showStaffUI ? [{ title: 'Load Cells', link: 'bins' }] : []),
    ...(showStaffUI ? [{ title: 'Deliveries', link: 'deliveries' }] : []),
    ...(showStaffUI ? [{ title: 'Staff Notes', link: 'comments' }] : []),
  ];

  const pageTitleSegments = useMemo(() => {
    const segments = [];

    if (barn?.name) {
      segments.push(censor(barn?.name, censorTypes.barn));
    }

    return segments;
  }, [barn]);

  useEffect(() => {
    let index = tabs.findIndex((tab) => window.location.pathname.includes(tab.link));
    index = index === -1 ? 0 : index;
    setSelectedTab(index);
  }, []);

  const orgDisplayName =
    showOrgName && barn?.organization?.name ? censor(barn?.organization?.name, censorTypes.organization) : '';
  const barnDisplayName = barn?.name ? censor(barn?.name, censorTypes.barn) : '';

  const IdWrappedBarnName = withStaffOnlyIdWrapper(barn?.name ? barnDisplayName : <LoadingSkeleton />);
  const IdWrapperOrgName = withStaffOnlyIdWrapper(barn?.organization?.name ? orgDisplayName : <LoadingSkeleton />);

  const pageDisplayHeader = (
    <div className="BarnPage-Header">
      <Heading>
        <IdWrappedBarnName data={barnId} hoverText="Copy Barn ID" />
        {showOrgName && <span>{' ('}</span>}
        <IdWrapperOrgName data={barn?.organization_id} hoverText="Copy Org ID" />
        {showOrgName && <span>{')'}</span>}
      </Heading>
      <div className="BarnPage-Right">
        <BellIcon
          className="pointer"
          ref={buttonRef}
          onClick={() => {
            setNotificationPopupOpen(!notificationPopupOpen);
          }}
          isSnoozed={barn?.user_barn_alert_snoozes?.length > 0}
          hasBarnTierAssignment={(barn?.user_notification_tier_assignments?.length ?? 0) > 0}
          hasOrgTierAssignment={(barn?.organization?.user_notification_tier_assignments?.length ?? 0) > 0}
          isNoNotificationsTier={
            barn?.user_notification_tier_assignments?.[0]?.tier?.name === NO_NOTIFICATIONS_TIER_NAME ||
            barn?.organization?.user_notification_tier_assignments?.[0]?.tier?.name === NO_NOTIFICATIONS_TIER_NAME
          }
        />
        {notificationPopupOpen && (
          <div className="Notification-popup" ref={popupRef}>
            <div className="Notification-popup-inner">
              {barn?.user_barn_alert_snoozes?.length == 0 && (
                <Select
                  defaultText="Select to Snooze"
                  itemList={[
                    { value: 4 * 60 * 60, name: '4 hours' },
                    { value: 1 * 24 * 60 * 60, name: '1 day' },
                    { value: 7 * 24 * 60 * 60, name: '7 days' },
                    { value: -2, name: 'Custom' },
                    { value: -1, name: 'Forever' },
                  ]}
                  label="Snooze for:"
                  onChange={(event) => {
                    const eventValue = parseInt(event.target.value);
                    const started_at = dayjs.tz().unix();
                    let ended_at = null;
                    if (eventValue > 0) {
                      ended_at = started_at + eventValue;
                    }

                    if (eventValue === -2) {
                      customSnoozeDialog?.current?.showModal();
                    } else {
                      createSnooze({
                        variables: {
                          started_at,
                          ended_at,
                        },
                        onCompleted: () => {
                          refetch({ now: dayjs.tz().unix() });
                        },
                      });
                    }
                  }}
                />
              )}
              {barn?.user_barn_alert_snoozes?.length > 0 && (
                <>
                  <p>
                    {snoozedUntil && (
                      <>
                        Notification will resume in{' '}
                        {dayjs.duration(dayjs.tz(1000 * snoozedUntil).diff(dayjs.tz())).humanize()}
                      </>
                    )}
                    {!snoozedUntil && <>Snoozed forever</>}
                  </p>
                  <Button
                    content="Resume Notifications"
                    color="error"
                    variant="vivid"
                    onClick={() => {
                      updateSnooze({
                        variables: {
                          ids: barn?.user_barn_alert_snoozes.map((snooze) => snooze.id),
                          ended_at: dayjs.tz().unix(),
                        },
                        onCompleted: () => {
                          refetch({ now: dayjs.tz().unix() });
                        },
                      });
                    }}
                  ></Button>
                </>
              )}
              <NotificationSettings
                barnId={barnId}
                userId={user?.id}
                onSave={() => {
                  refetch({ now: dayjs.tz().unix() });
                }}
                refetchMainQuery={refetch}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
  return (
    <Page className={`BarnPage ${className}`} headerComponent={pageDisplayHeader}>
      <FeedFloTabs tabData={tabs} defaultSelected={selectedTab} className={tabsClassName} />
      <Routes>
        <Route
          path="summary" //Support summary and status for now
          element={
            <BarnSummaryTab
              loading={loading}
              titleSegments={pageTitleSegments}
              barnId={barnId}
              organizationId={barn?.organization_id}
            />
          }
        />
        <Route
          path="status" //Support summary and status for now
          element={
            <BarnSummaryTab
              loading={loading}
              titleSegments={pageTitleSegments}
              barnId={barnId}
              organizationId={barn?.organization_id}
            />
          }
        />
        <Route
          path="consumption"
          element={
            <BarnConsumptionTab
              loading={loading}
              titleSegments={pageTitleSegments}
              barn={barn}
              barnId={barnId}
              orgId={barn?.organization_id}
              openFaultIds={openFaultIds}
              visibleFeedLineIds={visibleFeedLineIds}
              dateRangeOverride={dateRangeOverride}
              animalGroupId={animalGroupId}
              setAnimalGroupId={setAnimalGroupId}
            />
          }
        />
        <Route
          path="settings"
          element={
            <div>
              <BarnSettingsTab titleSegments={pageTitleSegments} barnId={barnId} />
            </div>
          }
        />
        <Route
          path="animals"
          element={
            <div>
              <AnimalGroupPage titleSegments={pageTitleSegments} barnId={barnId} orgId={barn?.organization_id} />
            </div>
          }
        />
        <Route path="inventory" element={<InventoryTabV2 titleSegments={pageTitleSegments} barnId={barnId} />} />
        <Route path="deliveries" element={<DeliveryTab titleSegments={pageTitleSegments} farmId={barnId} />} />
        <Route path="devices" element={<DeviceTab titleSegments={pageTitleSegments} barn={barn} barnId={barnId} />} />
        <Route path="bins" element={<BinTab titleSegments={pageTitleSegments} barn={barn} barnId={barnId} />} />
        <Route path="comments" element={<CommentsTab titleSegments={pageTitleSegments} barnId={barnId} />} />
        <Route path="calibrations" element={<CalibrationTab titleSegments={pageTitleSegments} barnId={barnId} />} />

        <Route path="devices" element={<DeviceTab titleSegments={pageTitleSegments} barn={barn} barnId={barnId} />} />
        <Route path="bins" element={<BinTab titleSegments={pageTitleSegments} barn={barn} barnId={barnId} />} />
        <Route path="comments" element={<CommentsTab titleSegments={pageTitleSegments} barnId={barnId} />} />
        <Route
          path="line/:lineId"
          element={<FeedLinePage titleSegments={pageTitleSegments} pageClass="no-margin" hideBarnName={true} />}
        />
        <Route path="calibrations">
          <Route index element={<CalibrationTab titleSegments={pageTitleSegments} barnId={barnId} />} />
          <Route path=":calibrationId">
            <Route index element={<CreateCalibration barnId={barnId} />} />
            <Route path="setup" element={<CreateCalibration barnId={barnId} />} />
            <Route path="test" element={<CalibrationResultView barnId={barnId} />} />
            <Route path="result" element={<CalibrationResultView barnId={barnId} />} />
          </Route>
        </Route>

        <Route path="" element={<Navigate to="summary" replace={true} />} />
      </Routes>
      <Dialog className="SnoozeNotifications-selectorDialog" ref={customSnoozeDialog}>
        <Formik
          initialValues={{ endTime: dayjs().add(1, 'day').format(HTML_DATE_TIME_INPUT) }}
          onSubmit={(values) => {
            const started_at = dayjs.tz().unix();
            const ended_at = dayjs.tz(values.endTime).unix();
            createSnooze({
              variables: {
                started_at,
                ended_at,
              },
              onCompleted: () => {
                refetch({ now: dayjs.tz().unix() });
              },
            });
          }}
        >
          {({ handleSubmit }) => {
            return (
              <>
                <h3>Custom Snooze Time</h3>
                <DateTimeInputField name="endTime" label={'Snooze End'} />
                <div className="SnoozeNotifications-selectorDialog--date">mm / dd / yyyy, hh : mm am</div>
                <div className="SnoozeNotifications-selectorDialog--buttons">
                  <Button
                    type="submit"
                    className="AnimalGroupSelector-submitButton"
                    variant="vivid"
                    content="Cancel"
                    onClick={() => {
                      customSnoozeDialog.current.close();
                    }}
                  />
                  <Button
                    type="submit"
                    className="AnimalGroupSelector-submitButton"
                    variant="vivid"
                    content="Done"
                    color="success"
                    onClick={() => {
                      handleSubmit();
                      customSnoozeDialog.current.close();
                    }}
                  />
                </div>
              </>
            );
          }}
        </Formik>
      </Dialog>
    </Page>
  );
}
BarnPage.propTypes = {
  className: PropTypes.string,
  tabsClassName: PropTypes.string,
  barnIdOverride: PropTypes.string,
  openFaultIds: PropTypes.arrayOf(PropTypes.string),
  visibleFeedLineIds: PropTypes.arrayOf(PropTypes.string),
  dateRangeOverride: PropTypes.shape({
    from: PropTypes.instanceOf(Dayjs).isRequired,
    to: PropTypes.instanceOf(Dayjs).isRequired,
  }),
};
export default BarnPage;
